import { Button, Grid, makeStyles, Snackbar, Typography } from '@rbx/ui';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import creatorFeedbackClient from '@modules/clients/creatorFeedbackClient';
import { useTranslation } from '@rbx/intl';
import { TestId } from '@modules/utilities/constants/testConstants';
import { useIsAuthenticated } from '@modules/assistant/context/IsAuthenticatedProvider';
import { useBrowserTabIdTracking } from '@modules/miscellaneous/common/components/BrowserTabIdTrackingProvider';
import ErrorSnackbar, {
  FeedbackUnavailableInYourLocaleErrorSnack,
} from '@modules/miscellaneous/common/components/ErrorSnackbar';
import safeLocalStorage from '@modules/utilities/utils/safeLocalStorage';
import { isInteractKey } from '@modules/layout/layout/utils/keyboardNavigationHandler';
import { FeedbackRating } from '@rbx/conv-ai-provider';
import { useShareFeedbackProvider } from '../context/ShareFeedbackProvider';
import FeedbackIcon from './FeedbackIcon';
import FeedbackThumbsUpDownIcon from './FeedbackThumbsUpDownIcon';

const useFeedbackButtonStyles = makeStyles()((theme) => ({
  feedbackButton: {
    marginRight: theme.spacing(1),
  },
  feedbackButtonRevamp: {
    color: theme.palette.content.muted,
  },
  feedbackButtonDisabled: {
    color: theme.palette.content.disabled,
  },
}));

interface FeedbackButtonProps {
  pageTitle: string;
  show: boolean;
  engineRevampEnabled?: boolean;
  /** Show "Provide feedback" text instead of "Feedback" */
  showProvideFeedbackText?: boolean;
}

const retryDelay = 3000;
const maxRetryWaitAuthLoadAttempts = 5;

// store browser tab ID + login redirect URI
const LatestFdbkLoginRedirectKey = 'RobloxDocs.feedbackLogin.redirect';
const delimiter = '|';
const setLatestFdbkLoginRedirect = (tabId: string) => {
  const value = [tabId, window.location.href].join(delimiter);
  safeLocalStorage.setItem(LatestFdbkLoginRedirectKey, value);
};
const clearLatestFdbkLoginRedirectValue = () => {
  safeLocalStorage.removeItem(LatestFdbkLoginRedirectKey);
};
const validateLatestFdbkLoginRedirectValue = (value?: string | null): string[] | false => {
  const vals = value ? value.split(delimiter) : null;
  if (!vals || !vals.length || vals.length !== 2) {
    // eslint-disable-next-line no-console
    console.error('Invalid login redirect instance value', value);
    // fail silently
    return false;
  }
  return vals;
};
const getLastFdbkLoginRedirectInfo = () => {
  const value = safeLocalStorage.getItem(LatestFdbkLoginRedirectKey);
  const validated = validateLatestFdbkLoginRedirectValue(value);
  if (validated) {
    return { tabId: validated[0], redirectUrl: validated[1] };
  }
  return {};
};

const FeedbackButton: FC<React.PropsWithChildren<FeedbackButtonProps>> = ({
  pageTitle,
  show,
  engineRevampEnabled = false,
  showProvideFeedbackText = false,
}) => {
  const { classes } = useFeedbackButtonStyles();
  const retryCount = useRef<number>(0);
  const { isAuthenticated, isAuthLoaded, login } = useIsAuthenticated();
  const { titleLength, isDocsFeedbackAllowed, hasFeedbackServiceError } =
    useShareFeedbackProvider();
  const { tabId, isReady: trackedTabIdIsReady } = useBrowserTabIdTracking();
  const [submitted, setSubmitted] = useState(false);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showUnallowedLocaleError, setShowUnallowedLocaleError] = useState(false);
  const { translate } = useTranslation();
  const { openFeedbackDialog } = useShareFeedbackProvider();

  const loginWithRedirectTracking = useCallback(() => {
    if (!tabId) return;
    // track redirect URL + browser tab ID
    setLatestFdbkLoginRedirect(tabId);
    login();
  }, [tabId, login]);
  const onSubmit = useCallback(
    async (ratingValue: FeedbackRating, comment?: string, options?: { isPublic: boolean }) => {
      if (submitted) return; // avoid dbl submission
      // feedback provider eagerly shows 'feedback submitted'
      // message before issue creation starts'
      // -> only handle error notification here
      setSubmitted(true);
      try {
        await creatorFeedbackClient.createIssue({
          pageTitle,
          titleLength,
          ratingValue,
          comment,
          isPublic: !!options?.isPublic,
        });
        setShowSubmissionSuccess(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        if (process.env.environment === 'development') console.error('Create issue error', err);
        setShowError(true);
      } finally {
        setSubmitted(false);
      }
    },
    [pageTitle, submitted, titleLength, setSubmitted],
  );
  const onClickFeedbackBtn = useCallback(() => {
    if (hasFeedbackServiceError) {
      setShowError(true);
      return;
    }
    if (!trackedTabIdIsReady) return;
    if (!isAuthLoaded) {
      // wait for auth to load, then retry
      if (retryCount.current <= maxRetryWaitAuthLoadAttempts) {
        setTimeout(() => {
          onClickFeedbackBtn();
        }, retryDelay);
      }
      return;
    }
    if (isAuthenticated && isDocsFeedbackAllowed) {
      openFeedbackDialog(undefined, onSubmit);
    } else if (!isAuthenticated) {
      loginWithRedirectTracking();
    }
  }, [
    isAuthenticated,
    isDocsFeedbackAllowed,
    onSubmit,
    hasFeedbackServiceError,
    isAuthLoaded,
    trackedTabIdIsReady,
    openFeedbackDialog,
    loginWithRedirectTracking,
  ]);

  useEffect(() => {
    const { tabId: lastFdbkLoginTabId, redirectUrl: lastFdbkLoginRedirectUrl } =
      getLastFdbkLoginRedirectInfo();
    if (!tabId || !isAuthenticated || isDocsFeedbackAllowed) return;
    if (lastFdbkLoginTabId === tabId && lastFdbkLoginRedirectUrl === window.location.href) {
      // user was redirected to this page after logging in, via a login redirect
      // from a prior click to this page's feedback button
      // --> (spec) given user's past attempt to log feedback, & the user is not allowed,
      // inform user that feedback is not available for them
      setShowUnallowedLocaleError(true);
      clearLatestFdbkLoginRedirectValue();
    }
  }, [tabId, isAuthenticated, isDocsFeedbackAllowed]);

  const onCloseFeedbackUnavailableInLocaleSnackbar = useCallback(
    () => setShowUnallowedLocaleError(false),
    [],
  );
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isInteractKey(e)) onClickFeedbackBtn();
    },
    [onClickFeedbackBtn],
  );

  if (!show || showUnallowedLocaleError)
    return (
      <FeedbackUnavailableInYourLocaleErrorSnack
        open={showUnallowedLocaleError}
        onClose={onCloseFeedbackUnavailableInLocaleSnackbar}
      />
    );

  const text = showProvideFeedbackText
    ? translate('Label.ProvideFeedback') || 'Provide feedback'
    : translate('Label.Feedback') || 'Feedback';

  const icon = engineRevampEnabled ? (
    <FeedbackThumbsUpDownIcon size={14} style={{ marginRight: 8, marginLeft: 2 }} />
  ) : (
    <FeedbackIcon size={14} style={{ marginLeft: 8, marginRight: 2 }} />
  );

  // Determine button class based on flags
  let buttonClassName = classes.feedbackButton;
  if (showProvideFeedbackText) {
    buttonClassName = classes.feedbackButtonDisabled;
  } else if (engineRevampEnabled) {
    buttonClassName = classes.feedbackButtonRevamp;
  }

  return (
    <Grid item XSmall='auto'>
      <Button
        aria-label={translate('Label.Feedback') || 'Feedback'}
        data-testid={TestId.FeedbackButton}
        onClick={onClickFeedbackBtn}
        onKeyDown={onKeyDown}
        color='primaryBrand'
        size='small'
        variant='text'
        className={buttonClassName}>
        {engineRevampEnabled ? (
          <React.Fragment>
            {icon}
            <Typography variant='body1' color='inherit'>
              {text}
            </Typography>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography variant='body1' color='inherit'>
              {text}
            </Typography>
            {icon}
          </React.Fragment>
        )}
      </Button>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSubmissionSuccess}
        onClose={() => setShowSubmissionSuccess(false)}
        message={translate('Message.FeedbackSubmitted')}
        autoHideDuration={3000}
      />
      <ErrorSnackbar
        open={showError}
        onClose={() => setShowError(false)}
        message={translate('Error.UnexpectedError')}
      />
      <ErrorSnackbar
        open={showUnallowedLocaleError}
        onClose={onCloseFeedbackUnavailableInLocaleSnackbar}
        message={translate('Error.FeedbackUnavailableInLocale')}
      />
    </Grid>
  );
};

export default FeedbackButton;
